import type { ComponentType, ReactNode } from 'react';
import type { ScreenDef } from './screens/types';
import type { IconName } from './design/icons';
import type { Client } from './api/client';

// ModuleUI — blueprint §2:
//   interface ModuleUI { name; nav: NavSection[]; screens: ScreenDef[];
//     widgets?: Record<string, ComponentType<any>>; commands?: CommandDef[] }

export interface NavItem {
  label: string;
  to: string;
  icon?: IconName;
  /** Exact-match highlighting (for '/'). */
  end?: boolean;
  /** Live badge: a number, or a query key the shell resolves via `badges`. */
  badge?: number | string;
  permission?: string;
  /** Keyboard chord for the "g then x" go-to shortcuts, e.g. 'i' → g i. */
  key?: string;
}

export interface NavSection {
  label: string;
  items: NavItem[];
  /** Collapsed by default in the sidebar. */
  collapsed?: boolean;
  order?: number;
  /**
   * The top-level area this section belongs to (see `shell/areas.ts` AREA_DEFS). Omitted →
   * the section is placed by its label (the SECTION_AREA table). A module's CONFIGURATION —
   * catalogs, templates, rules, settings — declares `area: 'config'` so it lands in the
   * Configuration area, grouped under the same headings as the operational areas, instead of
   * being mixed into day-to-day work menus.
   */
  area?: string;
}

export interface CommandContext {
  navigate: (to: string) => void;
  /** Open a form drawer for a resource (`?new=1` convention). */
  create: (resource: string) => void;
  close: () => void;
}

export interface CommandDef {
  id: string;
  label: string;
  /** Right-aligned hint ("go to", "create", …). */
  kind?: string;
  keywords?: string;
  icon?: IconName;
  /** Global shortcut, e.g. 'mod+shift+l'. */
  shortcut?: string;
  run: (ctx: CommandContext) => void;
  permission?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type WidgetComponent = ComponentType<any>;

/** One record hit in the ⌘K palette's live search. */
export interface SearchHit {
  id: string;
  label: string;
  /** Right-aligned hint, e.g. the resource label ("ticket"). */
  kind: string;
  icon?: IconName;
  keywords?: string;
  /** Route to open. */
  to: string;
}

/**
 * A module-provided record search for the palette (e.g. one round trip to a server-side
 * aggregate endpoint). When no module provides one, the palette fans `q=` out to every
 * searchable resource's list endpoint.
 */
export type SearchProvider = (client: Client, q: string) => Promise<SearchHit[]>;

/**
 * A dashboard tile a module contributes to Home (distinct from field `widgets`). A tile is a
 * summary that opens the rows behind it; it renders from the Home aggregate (`GET /v1/home`) and
 * returns null when there is nothing to show (the API omitted it, or the caller has no data).
 * Tiles sit in a band; a user may reorder or hide them (persisted as their `home` saved view).
 */
export interface TileDef {
  /** Stable, unique across modules: `<module>.<name>`. */
  key: string;
  /** The Home band it belongs to (`overview`, `my-week`, `attention`, …). */
  band: string;
  /** Name in the "Customise" list. */
  label: string;
  /** The Home aggregate is the home module's type; a contributing module narrows it. */
  render: (home: unknown) => ReactNode | null;
}

export interface ModuleUI {
  name: string;
  nav: NavSection[];
  screens: ScreenDef[];
  /** Custom field/cell/detail widgets, referenced by `FieldMeta.widget`. */
  widgets?: Record<string, WidgetComponent>;
  commands?: CommandDef[];
  /** Record search for the ⌘K palette; the first module that provides one wins. */
  search?: SearchProvider;
  /** Home tiles; Home composes every module's, in registration order, then the user's layout. */
  tiles?: TileDef[];
}

/** Helper for authoring with inference. */
export function defineModule(m: ModuleUI): ModuleUI {
  return m;
}
